import { useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { PlayerInput, PlayerRecord, PositionRecord } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useArchivePlayer,
  useBulkArchivePlayers,
  useBulkUpdatePlayers,
  useDeletePlayers,
  useConfig,
  useCreatePlayer,
  useCreatePlayerField,
  usePlayerFields,
  usePlayers,
  useResolvePositions,
  useSaveAttributes,
  useTeams,
  useUpdatePlayer,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { ageLabel, birthYear, formatBirthDate, sheetName } from '@/lib/format';
import { parseBirthDate } from '@shared/birthdate';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Sheet, SheetHead, SheetNumber, SheetRow } from '@/ui/Sheet';
import { Chip, PositionChip } from '@/ui/Chip';
import { BulkBar, RowCheck, useSelection } from '@/ui/Selection';

const blank: PlayerInput = { firstName: '', lastName: '' };

/** Seeded positions carry an i18n key; a coach's own additions keep their label. */
function positionLabel(
  pos: PositionRecord | undefined,
  t: (key: string) => string,
): string {
  if (!pos) return '';
  if (!pos.i18nKey) return pos.label;
  const translated = t(pos.i18nKey);
  return translated === pos.i18nKey ? pos.label : translated;
}

export function Squad({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const config = useConfig();
  const [teamId, setTeamId] = useState<string | null>(null);

  const teamList = teams.data ?? [];
  // Default to the first team rather than making the coach choose before seeing
  // anything; most coaches have one.
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  // Archiving is only honest if there is a way back to what was archived.
  const [showArchived, setShowArchived] = useState(false);
  const players = usePlayers(activeTeamId ?? undefined, showArchived);
  const create = useCreatePlayer();
  const update = useUpdatePlayer();
  const archive = useArchivePlayer();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editing, setEditing] = useState<PlayerRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<PlayerInput>(blank);
  // The birth date field keeps what the coach typed, not what it parsed to, so
  // a half-typed '03/20' is not rewritten under the cursor. It settles to the
  // stored form on blur.
  const [dobText, setDobText] = useState('');
  // The club's own fields, and this player's values for them.
  const fields = usePlayerFields();
  const saveAttributes = useSaveAttributes();
  const createField = useCreatePlayerField();
  const [attrs, setAttrs] = useState<Record<string, string>>({});
  const [newField, setNewField] = useState('');
  const resolvePositions = useResolvePositions();
  const bulkUpdate = useBulkUpdatePlayers();
  const bulkArchive = useBulkArchivePlayers();
  // One field at a time, deliberately: a bar that can change five things at once
  // is a form, and a form is what the row already is.
  const [bulkField, setBulkField] = useState<'statusKey' | 'roleKey' | 'primaryPositionKey'>('statusKey');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkResult, setBulkResult] = useState<{ updated: number; failed: number } | null>(null);
  const deletePlayers = useDeletePlayers();
  const [deleteResult, setDeleteResult] = useState<{ deleted: number; kept: number } | null>(null);
  const [repair, setRepair] = useState<{ fixed: number; unresolved: string[] } | null>(null);

  const positions = config.data?.positions ?? [];
  const allStatuses = config.data?.statuses ?? [];
  // One config table, three kinds. Mixing them put "Prospect" and "Trial" in the
  // list of this week's availability.
  const statuses = useMemo(
    () => allStatuses.filter((s) => s.kind === 'squad'),
    [allStatuses],
  );
  const roles = useMemo(() => allStatuses.filter((s) => s.kind === 'role'), [allStatuses]);
  const positionsByKey = useMemo(
    () => new Map(positions.map((p) => [p.key, p])),
    [positions],
  );

  const set = <K extends keyof PlayerInput>(k: K) => (value: PlayerInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const startCreate = () => {
    setEditing(null);
    setForm({ ...blank, teamId: activeTeamId ?? undefined });
    setDobText('');
    setAttrs({});
    setOpen(true);
  };

  const startEdit = (p: PlayerRecord) => {
    setEditing(p);
    setForm({
      firstName: p.firstName,
      lastName: p.lastName,
      knownAs: p.knownAs ?? undefined,
      dateOfBirth: p.dateOfBirth ?? undefined,
      nationality: p.nationality ?? undefined,
      strongFoot: (p.strongFoot as PlayerInput['strongFoot']) ?? undefined,
      heightCm: p.heightCm ?? undefined,
      weightKg: p.weightKg ?? undefined,
      primaryPositionKey: p.primaryPositionKey ?? undefined,
      secondaryPositionKey: p.secondaryPositionKey ?? undefined,
      notes: p.notes ?? undefined,
      teamId: activeTeamId ?? undefined,
      shirtNumber: p.shirtNumber ?? undefined,
      statusKey: p.statusKey ?? undefined,
      roleKey: p.roleKey ?? undefined,
    });
    setDobText(p.dateOfBirth ?? '');
    setAttrs({ ...(p.attributes ?? {}) });
    setOpen(true);
  };

  // What the typed birth date was understood to mean, shown back to the coach.
  // A list that gives only a year is normal; silently guessing a day is not.
  const dob = useMemo(() => {
    const typed = dobText.trim();
    if (!typed) return { hint: t('squad.dobHint') };
    const parsed = parseBirthDate(typed);
    if (!parsed) return { error: t('squad.dobUnreadable') };
    const age = ageLabel(parsed);
    const reads = t('squad.dobReads', { value: formatBirthDate(parsed, locale) });
    return { hint: age ? `${reads} · ${t('squad.dobAge', { label: age })}` : reads };
  }, [dobText, locale, t]);

  const list = players.data ?? [];
  // Imported before positions were resolved on the way in: the value is stored,
  // but it is not a key this club uses, so nothing can draw or group it.
  const unmatched = useMemo(
    () =>
      list.filter(
        (p) =>
          (p.primaryPositionKey && !positionsByKey.has(p.primaryPositionKey)) ||
          (p.secondaryPositionKey && !positionsByKey.has(p.secondaryPositionKey)),
      ).length,
    [list, positionsByKey],
  );
  // A squad reads in shirt-number order, the way a team sheet is printed;
  // unnumbered players fall to the end rather than sorting as zero.
  const ordered = useMemo(
    () =>
      [...list].sort((a, b) => {
        const an = a.shirtNumber ?? Number.POSITIVE_INFINITY;
        const bn = b.shirtNumber ?? Number.POSITIVE_INFINITY;
        if (an !== bn) return an - bn;
        return a.lastName.localeCompare(b.lastName, locale);
      }),
    [list, locale],
  );

  const selection = useSelection(useMemo(() => ordered.map((p) => p.id), [ordered]));

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
          title={t('squad.title')}
          lead={t('squad.lead')}
          help={localePath(locale, '/guide/squad')}
        />
        <Panel title={t('squad.title')}>
          <EmptyState title={t('teams.empty')} body={t('dashboard.noTeamBody')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('squad.title')}
        lead={t('squad.lead')}
        help={localePath(locale, '/guide/squad')}
        action={
          <Button variant="primary" onClick={startCreate}>
            {t('squad.add')}
          </Button>
        }
      />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`min-h-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
              }`}
            >
              {tm.name}
              <span className="tabular ms-2 opacity-70">{tm.playerCount}</span>
            </button>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="mb-5">
          <Panel title={editing ? t('squad.edit') : t('squad.add')}>
            <form
              noValidate
              className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  let playerId = editing?.id;
                  if (editing) {
                    await update.mutateAsync({ playerId: editing.id, patch: form });
                  } else {
                    playerId = (await create.mutateAsync(form)).id;
                  }
                  // Custom values are written after the player exists, and only
                  // for fields the club actually keeps.
                  const defs = fields.data ?? [];
                  if (playerId && defs.length > 0) {
                    const values: Record<string, string | null> = {};
                    for (const def of defs) values[def.key] = attrs[def.key] ?? null;
                    await saveAttributes.mutateAsync({ playerId, attributes: values });
                  }
                });
                if (ok) {
                  setOpen(false);
                  setEditing(null);
                }
              }}
            >
              {formError ? (
                <div className="sm:col-span-2 lg:col-span-3">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Field label={t('squad.firstName')} error={fieldError('firstName')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.firstName}
                    onChange={(e) => set('firstName')(e.target.value)}
                    required
                    autoFocus
                  />
                )}
              </Field>

              <Field label={t('squad.lastName')} error={fieldError('lastName')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.lastName}
                    onChange={(e) => set('lastName')(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field
                label={t('squad.knownAs')}
                hint={t('squad.knownAsHint')}
                optional
                error={fieldError('knownAs')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.knownAs ?? ''}
                    onChange={(e) => set('knownAs')(e.target.value || undefined)}
                  />
                )}
              </Field>

              <Field
                label={t('squad.shirtNumber')}
                optional
                error={fieldError('shirtNumber')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    min={1}
                    max={99}
                    inputMode="numeric"
                    value={form.shirtNumber ?? ''}
                    onChange={(e) =>
                      set('shirtNumber')(
                        e.target.value === '' ? undefined : Number(e.target.value),
                      )
                    }
                  />
                )}
              </Field>

              <Field
                label={t('squad.primaryPosition')}
                optional
                error={fieldError('primaryPositionKey')}
              >
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.primaryPositionKey ?? ''}
                    onChange={(e) =>
                      set('primaryPositionKey')(e.target.value || undefined)
                    }
                  >
                    <option value="">{t('squad.none')}</option>
                    {positions.map((pos) => (
                      <option key={pos.key} value={pos.key}>
                        {pos.key} · {positionLabel(pos, t)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field
                label={t('squad.secondaryPosition')}
                optional
                error={fieldError('secondaryPositionKey')}
              >
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.secondaryPositionKey ?? ''}
                    onChange={(e) =>
                      set('secondaryPositionKey')(e.target.value || undefined)
                    }
                  >
                    <option value="">{t('squad.none')}</option>
                    {positions.map((pos) => (
                      <option key={pos.key} value={pos.key}>
                        {pos.key} · {positionLabel(pos, t)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field
                label={t('squad.dateOfBirth')}
                optional
                error={fieldError('dateOfBirth') ?? dob.error}
                hint={dob.hint}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={dobText}
                    placeholder={t('squad.dobPlaceholder')}
                    autoComplete="off"
                    onChange={(e) => {
                      setDobText(e.target.value);
                      set('dateOfBirth')(parseBirthDate(e.target.value));
                    }}
                    onBlur={() => {
                      const parsed = parseBirthDate(dobText);
                      if (parsed) setDobText(parsed);
                    }}
                  />
                )}
              </Field>

              <Field label={t('squad.strongFoot')} optional error={fieldError('strongFoot')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.strongFoot ?? ''}
                    onChange={(e) =>
                      set('strongFoot')(
                        (e.target.value || undefined) as PlayerInput['strongFoot'],
                      )
                    }
                  >
                    <option value="">{t('squad.none')}</option>
                    <option value="right">{t('squad.foot_right')}</option>
                    <option value="left">{t('squad.foot_left')}</option>
                    <option value="both">{t('squad.foot_both')}</option>
                  </SelectInput>
                )}
              </Field>

              <Field label={t('squad.status')} optional error={fieldError('statusKey')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.statusKey ?? ''}
                    onChange={(e) => set('statusKey')(e.target.value || undefined)}
                  >
                    <option value="">{t('squad.none')}</option>
                    {statuses.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.i18nKey && t(s.i18nKey) !== s.i18nKey ? t(s.i18nKey) : s.label}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field label={t('squad.role')} optional hint={t('squad.roleHint')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.roleKey ?? ''}
                    onChange={(e) => set('roleKey')(e.target.value || undefined)}
                  >
                    <option value="">{t('squad.none')}</option>
                    {roles.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.i18nKey && t(r.i18nKey) !== r.i18nKey ? t(r.i18nKey) : r.label}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field label={t('squad.nationality')} optional error={fieldError('nationality')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.nationality ?? ''}
                    onChange={(e) => set('nationality')(e.target.value || undefined)}
                  />
                )}
              </Field>

              <Field label={t('squad.heightCm')} optional error={fieldError('heightCm')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    min={100}
                    max={230}
                    inputMode="numeric"
                    value={form.heightCm ?? ''}
                    onChange={(e) =>
                      set('heightCm')(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                  />
                )}
              </Field>

              <Field label={t('squad.weightKg')} optional error={fieldError('weightKg')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    min={25}
                    max={160}
                    inputMode="numeric"
                    value={form.weightKg ?? ''}
                    onChange={(e) =>
                      set('weightKg')(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                  />
                )}
              </Field>

              <div className="sm:col-span-2 lg:col-span-3">
                <Field
                  label={t('squad.notes')}
                  hint={t('squad.notesHint')}
                  optional
                  error={fieldError('notes')}
                >
                  {(p) => (
                    <TextArea
                      {...p}
                      value={form.notes ?? ''}
                      onChange={(e) => set('notes')(e.target.value || undefined)}
                    />
                  )}
                </Field>
              </div>

              <div className="border-t border-line pt-4 sm:col-span-2 lg:col-span-3">
                <h3 className="font-condensed text-subhead font-semibold tracking-tight">
                  {t('squad.customFields')}
                </h3>
                <p className="mt-1 max-w-[72ch] text-meta text-ink-muted">
                  {t('squad.customFieldsHint')}
                </p>
                <div className="mt-3 grid gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(fields.data ?? []).map((def) => (
                    <Field
                      key={def.id}
                      label={def.label}
                      optional
                      hint={def.isSensitive ? t('squad.sensitiveField') : undefined}
                    >
                      {(p) => (
                        <TextInput
                          {...p}
                          // Always text. A club's own field is whatever the club
                          // puts in it — a licence number reading MA-2026-4471
                          // is not a number, and a numeric input would swallow
                          // it. The kind only picks the on-screen keyboard.
                          type={def.kind === 'email' ? 'email' : def.kind === 'phone' ? 'tel' : 'text'}
                          inputMode={def.kind === 'number' ? 'numeric' : undefined}
                          value={attrs[def.key] ?? ''}
                          onChange={(e) =>
                            setAttrs((a) => ({ ...a, [def.key]: e.target.value }))
                          }
                        />
                      )}
                    </Field>
                  ))}

                  <Field label={t('squad.addField')} optional>
                    {(p) => (
                      <div className="flex gap-2">
                        <TextInput
                          {...p}
                          value={newField}
                          placeholder={t('squad.newFieldName')}
                          onChange={(e) => setNewField(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            if (!newField.trim()) return;
                            void createField
                              .mutateAsync({ label: newField.trim() })
                              .then(() => setNewField(''));
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!newField.trim()}
                          busy={createField.isPending}
                          onClick={() =>
                            void createField
                              .mutateAsync({ label: newField.trim() })
                              .then(() => setNewField(''))
                          }
                        >
                          +
                        </Button>
                      </div>
                    )}
                  </Field>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-3">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('squad.save')}
                </Button>
                <Button
                  type="button"
                  variant="quiet"
                  onClick={() => {
                    setOpen(false);
                    setEditing(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                {editing ? (
                  <Button
                    type="button"
                    variant="danger"
                    className="ms-auto"
                    onClick={async () => {
                      if (!confirm(t('squad.archiveConfirm'))) return;
                      await archive.mutateAsync(editing.id);
                      setOpen(false);
                      setEditing(null);
                    }}
                  >
                    {t('squad.archive')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      {unmatched > 0 || repair ? (
        <div className="mb-4">
          <Panel title={t('squad.fixPositions')}>
            <div className="flex flex-wrap items-center gap-3 p-4">
              <p className="min-w-0 flex-1 text-meta text-ink-muted">
                {repair
                  ? t('squad.positionsFixed', { count: repair.fixed })
                  : t('squad.fixPositionsHint', { count: unmatched })}
                {repair && repair.unresolved.length > 0
                  ? ` ${t('squad.positionsUnresolved', { values: repair.unresolved.join(', ') })}`
                  : ''}
              </p>
              {unmatched > 0 ? (
                <Button
                  variant="primary"
                  busy={resolvePositions.isPending}
                  onClick={() =>
                    void resolvePositions
                      .mutateAsync(activeTeamId ?? undefined)
                      .then((r) => setRepair({ fixed: r.fixed, unresolved: r.unresolved }))
                  }
                >
                  {t('squad.fixPositions')}
                </Button>
              ) : null}
            </div>
          </Panel>
        </div>
      ) : null}

      <Panel
        title={teamList.find((tm) => tm.id === activeTeamId)?.name ?? t('squad.title')}
        count={t('squad.count', { count: ordered.length })}
        action={
          <div className="flex gap-1.5">
            {[false, true].map((archived) => (
              <button
                key={String(archived)}
                type="button"
                aria-pressed={showArchived === archived}
                onClick={() => {
                  setShowArchived(archived);
                  selection.clear();
                  setBulkResult(null);
                }}
                className={`min-h-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                  showArchived === archived
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                }`}
              >
                {t(archived ? 'squad.showArchived' : 'squad.showActive')}
              </button>
            ))}
          </div>
        }
      >
        {players.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : players.isError ? (
          <div className="p-4">
            <Notice tone="risk">{t('error.generic')}</Notice>
          </div>
        ) : ordered.length === 0 ? (
          <EmptyState
            title={t(showArchived ? 'squad.archivedEmpty' : 'squad.empty')}
            body={t(showArchived ? 'squad.archivedEmptyBody' : 'squad.emptyBody')}
            action={
              showArchived ? undefined : (
                <Button variant="primary" onClick={startCreate}>
                  {t('squad.add')}
                </Button>
              )
            }
          />
        ) : (
          <>
            <SheetHead>
              <RowCheck
                checked={selection.isAll}
                onChange={() => (selection.isAll ? selection.clear() : selection.all())}
                label={t('bulk.selectAll')}
              />
              <span className="w-8 shrink-0">{t('squad.number')}</span>
              <span className="flex-1">{t('squad.player')}</span>
              <span className="hidden w-16 shrink-0 sm:block">{t('squad.position')}</span>
              <span className="tabular hidden w-14 shrink-0 text-end sm:block">
                {t('squad.age')}
              </span>
              <span className="hidden w-24 shrink-0 text-end md:block">
                {t('squad.status')}
              </span>
            </SheetHead>
            <Sheet>
              {ordered.map((p) => {
                const primary = positionsByKey.get(p.primaryPositionKey ?? '');
                const secondary = positionsByKey.get(p.secondaryPositionKey ?? '');
                const status = allStatuses.find((s) => s.key === p.statusKey);
                const age = ageLabel(p.dateOfBirth);
                return (
                  <SheetRow key={p.id} onClick={() => startEdit(p)}>
                    <RowCheck
                      checked={selection.has(p.id)}
                      onChange={() => selection.toggle(p.id)}
                      label={t('bulk.selectRow', {
                        name: sheetName(p.knownAs ?? p.firstName, p.lastName),
                      })}
                    />
                    <SheetNumber>{p.shirtNumber ?? '–'}</SheetNumber>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                      </span>
                      <span className="flex items-center gap-2 text-meta text-ink-faint sm:hidden">
                        {primary ? (
                          <PositionChip code={primary.key} unit={primary.unit} />
                        ) : null}
                        <span className="tabular">{birthYear(p.dateOfBirth)}</span>
                        {status ? <span>{status.i18nKey && t(status.i18nKey) !== status.i18nKey ? t(status.i18nKey) : status.label}</span> : null}
                      </span>
                    </span>
                    <span className="hidden w-16 shrink-0 items-center gap-1 sm:flex">
                      {primary ? (
                        <PositionChip code={primary.key} unit={primary.unit} />
                      ) : p.primaryPositionKey ? (
                        // Stored, but not a position this club uses. Showing it
                        // is how the coach finds out it needs fixing.
                        <span className="truncate font-condensed text-meta text-warn">
                          {p.primaryPositionKey}
                        </span>
                      ) : (
                        <span className="text-ink-faint">{t('squad.none')}</span>
                      )}
                      {secondary ? (
                        <span className="tabular font-condensed text-meta text-ink-faint">
                          {secondary.key}
                        </span>
                      ) : null}
                    </span>
                    <span className="tabular hidden w-14 shrink-0 text-end font-condensed text-meta text-ink-muted sm:block">
                      {age ?? '–'}
                    </span>
                    <span className="hidden w-24 shrink-0 justify-end md:flex">
                      {status ? (
                        <Chip muted>
                          {status.i18nKey && t(status.i18nKey) !== status.i18nKey
                            ? t(status.i18nKey)
                            : status.label}
                        </Chip>
                      ) : null}
                    </span>
                  </SheetRow>
                );
              })}
            </Sheet>
          </>
        )}
      </Panel>

      <BulkBar count={selection.count} onClear={selection.clear}>
        <SelectInput
          aria-label={t('squad.status')}
          className="w-36"
          value={bulkField}
          onChange={(e) => {
            setBulkField(e.target.value as typeof bulkField);
            setBulkValue('');
            setBulkResult(null);
          }}
        >
          <option value="statusKey">{t('squad.status')}</option>
          <option value="roleKey">{t('squad.role')}</option>
          <option value="primaryPositionKey">{t('squad.position')}</option>
        </SelectInput>

        <SelectInput
          aria-label={t('bulk.apply')}
          className="w-40"
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
        >
          <option value="">{t('bulk.noChange')}</option>
          {bulkField === 'primaryPositionKey'
            ? positions.map((pos) => (
                <option key={pos.key} value={pos.key}>
                  {positionLabel(pos, t)}
                </option>
              ))
            : (bulkField === 'roleKey' ? roles : statuses).map((option) => (
                <option key={option.key} value={option.key}>
                  {option.i18nKey && t(option.i18nKey) !== option.i18nKey
                    ? t(option.i18nKey)
                    : option.label}
                </option>
              ))}
        </SelectInput>

        <Button
          variant="primary"
          busy={bulkUpdate.isPending}
          disabled={!bulkValue}
          onClick={() =>
            void bulkUpdate
              .mutateAsync({
                playerIds: [...selection.selected],
                patch: {
                  // The team is needed for squad-scoped fields to mean anything.
                  ...(activeTeamId ? { teamId: activeTeamId } : {}),
                  [bulkField]: bulkValue,
                },
              })
              .then((r) => {
                setBulkResult({ updated: r.updated, failed: r.failed.length });
                setBulkValue('');
              })
          }
        >
          {t('bulk.apply')}
        </Button>

        <Button
          variant={showArchived ? 'secondary' : 'danger'}
          busy={bulkArchive.isPending}
          onClick={() => {
            if (
              !showArchived &&
              !window.confirm(t('bulk.confirmArchive', { count: selection.count }))
            ) {
              return;
            }
            void bulkArchive
              .mutateAsync({ playerIds: [...selection.selected], archived: !showArchived })
              .then((r) => {
                setBulkResult({ updated: r.updated, failed: 0 });
                selection.clear();
              });
          }}
        >
          {t(showArchived ? 'bulk.restore' : 'bulk.archive')}
        </Button>

        <Button
          variant="danger"
          busy={deletePlayers.isPending}
          onClick={() => {
            if (!window.confirm(t('squad.confirmDelete', { count: selection.count }))) return;
            void deletePlayers.mutateAsync([...selection.selected]).then((r) => {
              setDeleteResult({ deleted: r.deleted, kept: r.kept.length });
              selection.clear();
            });
          }}
        >
          {t('squad.delete')}
        </Button>

        {bulkResult ? (
          <span className="text-meta text-ink-muted">
            {t('bulk.applied', { count: bulkResult.updated })}
            {bulkResult.failed > 0
              ? ` · ${t('bulk.someFailed', { count: bulkResult.failed })}`
              : ''}
          </span>
        ) : null}

        {deleteResult ? (
          <span className="text-meta text-ink-muted">
            {t('squad.deleted', { count: deleteResult.deleted })}
            {deleteResult.kept > 0
              ? ` · ${t('squad.keptWithHistory', { count: deleteResult.kept })}`
              : ''}
          </span>
        ) : null}
      </BulkBar>
    </>
  );
}
